import { useEffect, useRef, type ReactNode } from 'react';

interface LiquidLens {
  destroy?: () => void;
}

type LiquidFactory = (opts: Record<string, unknown>) => LiquidLens | LiquidLens[];

let counter = 0;

/**
 * Glass pane with an always-readable foreground.
 *
 * Structure: the host (fixed/sticky via CSS) carries the CSS glass fallback
 * (backdrop-filter + tint). Inside it sit an EMPTY lens layer and the real
 * content above it. liquid-gl is pointed at the empty lens layer only: it
 * anchors to the nearest fixed/sticky ancestor (the host), hides the lens
 * layer while it snapshots and fades it in — none of which touches the
 * content, so labels and icons are never hidden, dimmed or redrawn through
 * the shader and stay pixel-crisp.
 *
 * `snapshot` limits what may be refracted: the hero button passes the hero
 * photo only, so letters of neighbouring sections never appear in it.
 * liquid-gl is skipped for prefers-reduced-motion,
 * prefers-reduced-transparency and `?glass=css`; the CSS glass remains.
 */
export function GlassSurface({
  children,
  className = '',
  snapshot,
  zIndex,
  as: Tag = 'div',
  enabled = true,
  tint = 'rgba(10, 12, 16, 0.28)',
  frost = 2,
  ...rest
}: {
  children: ReactNode;
  className?: string;
  snapshot: string;
  zIndex: number;
  as?: 'div' | 'nav';
  enabled?: boolean;
  tint?: string;
  frost?: number;
} & Record<`aria-${string}`, string>) {
  const hostRef = useRef<HTMLElement | null>(null);
  const idRef = useRef(`glass-${++counter}`);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !enabled) return;
    const reduce =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      window.matchMedia('(prefers-reduced-transparency: reduce)').matches ||
      new URLSearchParams(location.search).get('glass') === 'css';
    if (reduce) return;

    let lens: LiquidLens | LiquidLens[] | undefined;
    let cancelled = false;
    const start = async () => {
      try {
        const mod = (await import('liquid-gl')) as unknown as { default: LiquidFactory };
        if (cancelled || !document.querySelector(snapshot)) return;
        lens = mod.default({
          target: `[data-glass-id="${idRef.current}"]`,
          snapshot,
          zIndex,
          resolution: 1.5,
          refraction: 0.012,
          bevelDepth: 0.08,
          bevelWidth: 0.18,
          frost,
          tint,
          shadow: false,
          specular: true,
          reveal: 'fade',
        });
        host.dataset.glass = 'liquid';
      } catch (err) {
        console.info('[glass] liquid-gl unavailable, CSS glass kept', err);
      }
    };
    // Let images/fonts settle first: the library snapshots once.
    const idle = 'requestIdleCallback' in window ? window.requestIdleCallback : (cb: () => void) => setTimeout(cb, 300);
    idle(() => void start());

    return () => {
      cancelled = true;
      for (const l of Array.isArray(lens) ? lens : lens ? [lens] : []) l.destroy?.();
      delete host.dataset.glass;
    };
  }, [snapshot, zIndex, enabled, tint, frost]);

  return (
    <Tag ref={hostRef as never} className={`glass ${className}`} style={{ zIndex }} {...rest}>
      <span className="glass__lens" data-glass-id={idRef.current} aria-hidden />
      <span className="glass__content">{children}</span>
    </Tag>
  );
}
